import { Box, Dialog, IconButton, Stack, Typography } from '@mui/material';
import { DataGridPro, type DataGridProProps, type GridRowParams } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import type { Bet, BettingSlipWithMonitoringInfo } from '../../@types/api';

import { Close, ExpandLess, ExpandMore, SettingsOutlined } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { upperCase } from 'lodash-es';
import { StyleObj } from '../../@types';
import { QUERY_KEYS } from '../../constants';
import { useModal } from '../../contexts/ModalContext';
import { getTicketBettingType } from '../../helpers';
import { defaultColumnsTicketDetails } from '../../helpers/table';
import { createTicketDetailsColumns } from '../../helpers/ticketDetailsColumns';
import { betBuilderGroupRows, isBetBuilderRow } from '../../helpers/ticketMonitoring';
import { useBreakpoints } from '../../hooks/useBreakpoints';
import useVisibleColumns from '../../hooks/useVisibleColumns';
import { dataGridCellAlignmentSx, dataGridProSx } from '../../styles/mixins';
import { getBettingSlipStatusColor } from '../../utils';
import { getData } from '../../utils/api';
import { mergeSx } from '../../utils/styles';
import FullScreenLoader from '../atoms/loaders/FullScreenLoader';
import TicketDetailsInfoItem from '../atoms/TicketDetailsInfoItem';
import CustomizeTable from '../molecules/CustomizeTable';
import BetBuilderSelectionsList from '../molecules/BetBuilderSelectionsList';
import CollapsibleAuthResults from '../molecules/CollapsibleAuthResults';

const getDetailPanelHeight = (): 'auto' => 'auto';

const styles: StyleObj = {
  generalInfoRow: {
    backgroundColor: 'primary.main',
    color: 'white',
    display: 'flex',
    justifyContent: 'space-between',
    py: 0.5,
    width: '100%',
  },
  generalInfoWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    px: 2,
    width: '100%',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    backgroundColor: 'background.lighter',
    p: 1.5,
  },
  dialog: {
    '& .MuiDialog-paper': {
      width: 'unset',
      borderRadius: {
        xs: 0,
        md: 2,
      },
      boxShadow: 0,
      m: 0,
      overflowX: 'auto',
    },
  },
  closeBtn: {
    color: 'white',
    pr: 2,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  dialogContent: {
    minWidth: (theme) => theme.breakpoints.values.xl,
  },
};

type TicketDetailsModalProps = {
  id: string;
  onClose: () => void;
};

const TicketDetailsModal = ({ id, onClose }: TicketDetailsModalProps) => {
  const [open, setOpen] = useState(true);

  const { openModal } = useModal();
  const { isMobile } = useBreakpoints();

  const { data: ticketDetails, isPending } = useQuery({
    queryKey: [QUERY_KEYS.bettingSlips, id],
    queryFn: (): Promise<BettingSlipWithMonitoringInfo> => getData(`/betting-slips/${id}`),
    enabled: !!id,
  });

  const reofferedFromId = ticketDetails?.reofferedFromId;

  const { data: reofferedFromTicketDetails } = useQuery({
    queryKey: [QUERY_KEYS.bettingSlips, reofferedFromId],
    queryFn: (): Promise<BettingSlipWithMonitoringInfo> => getData(`/betting-slips/${reofferedFromId}`),
    enabled: !!reofferedFromId,
  });

  const closeModal = () => {
    setOpen(false);
    onClose();
  };

  const handleClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick') return;
    closeModal();
  };

  const columns = useMemo(
    () => createTicketDetailsColumns({ reofferedFromTicketDetails }),
    [reofferedFromTicketDetails]
  );

  const rows = useMemo(
    () => [...(ticketDetails?.bets || []), ...betBuilderGroupRows(ticketDetails?.betBuilder)],
    [ticketDetails]
  );

  const { visibleColumns, setVisibleColumns, visibleColumnsLSKey } = useVisibleColumns({
    manualLSKey: 'ticket-monitoring-details-table',
    columns,
    defaultVisibleColumns: defaultColumnsTicketDetails,
  });

  const openCustomizeTableModal = () => {
    openModal(
      <CustomizeTable
        columns={columns}
        defaultVisibleColumns={defaultColumnsTicketDetails}
        setVisibleColumns={setVisibleColumns}
        visibleColumns={visibleColumns}
        visibleColumnsLSKey={visibleColumnsLSKey}
      />
    );
  };

  const getDetailPanelContent = useCallback((params: GridRowParams<Bet>) => {
    if (!isBetBuilderRow(params.row)) return null;

    return <BetBuilderSelectionsList selections={params.row.selections} />;
  }, []);

  const infoItems = [
    {
      label: 'Stake',
      oldValue: reofferedFromTicketDetails?.totalStakeAmount?.toFixed(2),
      newValue: ticketDetails?.totalStakeAmount?.toFixed(2),
    },
    {
      label: 'Odds',
      oldValue: reofferedFromTicketDetails?.totalOdds?.toFixed(2),
      newValue: ticketDetails?.totalOdds?.toFixed(2),
    },
    {
      label: 'Combinations',
      oldValue: reofferedFromTicketDetails?.combinationsCount,
      newValue: ticketDetails?.combinationsCount,
    },
    {
      label: 'Bonus',
      oldValue: reofferedFromTicketDetails?.bonus?.bonusAmount?.toFixed(2),
      newValue: ticketDetails?.bonus?.bonusAmount?.toFixed(2),
      show: !!ticketDetails?.bonus,
    },
    {
      label: 'Possible Winnings',
      oldValue: reofferedFromTicketDetails?.possibleWinnings?.toFixed(2),
      newValue: ticketDetails?.possibleWinnings?.toFixed(2),
    },
    {
      label: 'Winnings',
      oldValue: reofferedFromTicketDetails?.winnings?.toFixed(2),
      newValue: ticketDetails?.winnings?.toFixed(2),
    },
  ].filter((item) => item.show !== false);

  if (isPending) return <FullScreenLoader />;

  return (
    <Dialog open={open} onClose={handleClose} sx={styles.dialog} fullWidth maxWidth='xl'>
      <Box sx={styles.dialogContent}>
        <Box sx={styles.generalInfoRow}>
          {ticketDetails && (
            <Box sx={styles.generalInfoWrapper}>
              <Typography variant='h5'>Username: {ticketDetails.user?.username}</Typography>
              <Typography variant='h5'>Type: {getTicketBettingType(ticketDetails)}</Typography>
              <Typography variant='h5'>From: {ticketDetails.from}</Typography>
              {ticketDetails.operator && (
                <Typography variant='h5'>Betting Operator: {ticketDetails.operator.fullName}</Typography>
              )}
            </Box>
          )}
          <Stack direction='row'>
            <IconButton onClick={openCustomizeTableModal} color='inherit' size='small'>
              <SettingsOutlined />
            </IconButton>
            <IconButton onClick={closeModal} sx={styles.closeBtn}>
              <Close />
            </IconButton>
          </Stack>
        </Box>
        <Box sx={styles.infoRow}>
          {infoItems.map((item) => (
            <TicketDetailsInfoItem
              key={item.label}
              label={item.label}
              oldValue={item.oldValue}
              newValue={item.newValue}
            />
          ))}
          <Typography variant='h5' sx={{ color: getBettingSlipStatusColor(ticketDetails?.status || 'open') }}>
            {upperCase(ticketDetails?.status)}
          </Typography>
        </Box>
        <DataGridPro
          rows={rows}
          loading={isPending}
          columns={columns}
          hideFooterPagination
          columnVisibilityModel={visibleColumns}
          disableColumnMenu
          showColumnVerticalBorder
          rowHeight={isMobile ? 44 : 48}
          columnHeaderHeight={isMobile ? 44 : 56}
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={getDetailPanelHeight}
          slots={{
            detailPanelExpandIcon: () => <ExpandMore />,
            detailPanelCollapseIcon: () => <ExpandLess />,
          }}
          sx={mergeSx(dataGridProSx, dataGridCellAlignmentSx) as DataGridProProps['sx']}
        />
        <CollapsibleAuthResults
          authResults={ticketDetails?.authResults || []}
          operator={ticketDetails?.operator || null}
        />
      </Box>
    </Dialog>
  );
};

export default TicketDetailsModal;
